import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, History } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Dialog, MultiSelect, TextField, Tooltip } from 'components';
import { useAppSelector, useSystem } from 'store';
import { useGetConversationsQuery, useSearchConversationsQuery } from 'store/services/LLMService';
import { useGetUsersQuery } from 'store/services/UserService';
import { UserRole } from 'models';

// Local interface matching the LLMService Conversation type
interface Conversation {
	conversation_id: string;
	user_id: number;
	user_email?: string | null;
	user_name?: string | null;
	facility_id?: number | null;
	healthsystem_id?: number | null;
	facility_name?: string | null;
	healthsystem_name?: string | null;
	title: string;
	created_at: string;
	last_updated_at: string;
}

export interface ChatHistoryDialogProps {
	onSelectConversation: (conversation: Conversation) => void;
	children?: React.ReactNode;
}

// Custom pagination state interface
interface PaginationState {
	page: number;
	limit: number;
}

interface UserFilterOption {
	label: string;
	value: number;
}

const ITEMS_PER_PAGE = 5;

/**
 * Dialog for viewing chat conversation history.
 * Click on any conversation to view it directly.
 */
export function ChatHistoryDialog({ children, onSelectConversation }: ChatHistoryDialogProps) {
	const [dialogOpen, setDialogOpen] = useState(false);
	const { data: systemData } = useSystem();
	const { selectedSystem } = useAppSelector((state) => state.userState);
	const canUseGlobalView = systemData?.user?.role === UserRole.super_admin;
	const [globalView, setGlobalView] = useState(false);
	const [selectedUserOptions, setSelectedUserOptions] = useState<UserFilterOption[]>([]);

	// Search state
	const [searchQuery, setSearchQuery] = useState('');
	const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

	// Debounce search query (300ms delay)
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchQuery(searchQuery);
		}, 300);

		return () => clearTimeout(timer);
	}, [searchQuery]);

	// Custom pagination state
	const [pagination, setPagination] = useState<PaginationState>({
		page: 1,
		limit: ITEMS_PER_PAGE,
	});

	useEffect(() => {
		if (!canUseGlobalView && globalView) {
			setGlobalView(false);
		}
		if (!canUseGlobalView) {
			setSelectedUserOptions([]);
		}
	}, [canUseGlobalView, globalView]);

	useEffect(() => {
		setSelectedUserOptions([]);
		setPagination({ page: 1, limit: ITEMS_PER_PAGE });
	}, [selectedSystem]);

	const { data: usersData, isFetching: isUsersFetching } = useGetUsersQuery(
		{
			healthsystem_id: selectedSystem,
			short: true,
		},
		{
			skip: !dialogOpen || !canUseGlobalView || !globalView || !selectedSystem,
		}
	);

	const userOptions = useMemo<UserFilterOption[]>(
		() =>
			(usersData?.users ?? [])
				.filter((user) => typeof user.id === 'number' && Boolean(user.email))
				.map((user) => ({
					label: user.email,
					value: user.id,
				})),
		[usersData?.users]
	);

	const selectedUserIds = useMemo(() => selectedUserOptions.map((option) => option.value), [selectedUserOptions]);
	const isGlobalViewActive = canUseGlobalView && globalView;

	// Determine if we're in search mode
	const isSearchMode = debouncedSearchQuery.trim().length > 0;

	// Fetch conversations with pagination (when not searching)
	const { data: conversationsData, isFetching: isConversationsFetching } = useGetConversationsQuery(
		{
			page: pagination.page,
			limit: pagination.limit,
			global: isGlobalViewActive,
			user_ids: isGlobalViewActive ? selectedUserIds : undefined,
		},
		{
			skip: isSearchMode,
		}
	);

	// Fetch search results (when searching)
	const {
		data: searchData,
		isLoading: isSearchLoading,
		isFetching: isSearchFetching,
	} = useSearchConversationsQuery(
		{
			q: debouncedSearchQuery,
			limit: ITEMS_PER_PAGE,
			global: isGlobalViewActive,
			user_ids: isGlobalViewActive ? selectedUserIds : undefined,
		},
		{
			skip: !isSearchMode,
		}
	);

	// Determine which data to use based on search mode
	const rawConversations = isSearchMode
		? searchData?.results ?? conversationsData?.conversations ?? []
		: conversationsData?.conversations ?? [];

	// Create a dummy conversation when in search mode and there are no results
	const conversations =
		isSearchMode && rawConversations.length === 0
			? [
					{
						conversation_id: 'dummy',
						user_id: 0,
						title: 'No conversations found',
						created_at: '',
						last_updated_at: '',
					},
			  ]
			: rawConversations;
	const totalCount = isSearchMode ? searchData?.total_count ?? 0 : conversationsData?.total_count ?? 0;
	const totalPages = isSearchMode ? 1 : conversationsData?.total_pages ?? 0;
	const hasNext = isSearchMode ? false : conversationsData?.has_next ?? false;
	const hasPrev = isSearchMode ? false : conversationsData?.has_prev ?? false;
	// Use isFetching to show loading state on every request (not just first load)
	const isWorking = isSearchMode ? isSearchFetching : isConversationsFetching;

	// Compute page numbers for display
	const pageInfo = useMemo(() => {
		const start = (pagination.page - 1) * pagination.limit + 1;
		const end = Math.min(pagination.page * pagination.limit, totalCount);
		return { start, end };
	}, [pagination.page, pagination.limit, totalCount]);

	// Handle page change
	const handlePageChange = (newPage: number) => {
		if (newPage >= 1 && newPage <= totalPages) {
			setPagination((prev) => ({ ...prev, page: newPage }));
		}
	};

	// Reset to first page when dialog opens
	const handleOpenChange = (isBeingOpened: boolean) => {
		if (isBeingOpened) {
			setDialogOpen(true);
			setPagination({ page: 1, limit: ITEMS_PER_PAGE });
			return;
		}
		setDialogOpen(false);
	};

	// Default trigger if no children provided
	const defaultTrigger = (
		<button className='group relative flex items-center justify-center h-8 w-10 rounded-xl text-blue-500 dark:text-blue-400 hover:bg-blue-100/80 dark:hover:bg-blue-700/80 transition-all duration-300 hover:scale-110 hover:text-blue-700 dark:hover:text-blue-200'>
			<Tooltip content={'Chat History'} triggerType='span'>
				<History size={16} />
			</Tooltip>
		</button>
	);

	// Handle row click to view conversation directly
	const handleRowClick = (conversation: Conversation) => {
		if (conversation.conversation_id === 'dummy') return;
		onSelectConversation(conversation);
		setDialogOpen(false);
	};

	const handleGlobalViewToggle = () => {
		setGlobalView((current) => {
			if (current) {
				setSelectedUserOptions([]);
			}
			return !current;
		});
		setPagination({ page: 1, limit: ITEMS_PER_PAGE });
	};

	const handleUserFilterChange = (options: readonly UserFilterOption[]) => {
		setSelectedUserOptions([...options]);
		setPagination({ page: 1, limit: ITEMS_PER_PAGE });
	};

	const globalViewDescription = selectedUserOptions.length
		? `Showing conversations from ${selectedUserOptions.length} selected user${
				selectedUserOptions.length === 1 ? '' : 's'
		  }`
		: 'Showing conversations from all users';

	const parseUtcTimestamp = (dateString: string) => {
		const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(dateString);
		return new Date(hasTimezone ? dateString : `${dateString}Z`);
	};

	// Format date for display
	const formatDate = (dateString: string) => {
		return parseUtcTimestamp(dateString).toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
		});
	};

	const formatConversationContext = (conversation: Conversation) => {
		const facilityLabel =
			conversation.facility_name ?? (conversation.facility_id ? `Facility #${conversation.facility_id}` : null);
		const healthsystemLabel =
			conversation.healthsystem_name ??
			(conversation.healthsystem_id ? `Healthsystem #${conversation.healthsystem_id}` : null);

		return [facilityLabel, healthsystemLabel].filter(Boolean).join(' • ');
	};

	const formatConversationOwner = (conversation: Conversation) => {
		const name = conversation.user_name?.trim();
		const email = conversation.user_email?.trim();

		if (name && email) return `${name} • ${email}`;
		return name || email || '';
	};

	// Render pagination controls
	const renderPagination = () => {
		if (totalPages <= 1) return null;

		return (
			<div className='flex items-center justify-between px-4 py-3 border-t border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-800/50'>
				<div className='text-sm text-blue-500 dark:text-blue-400'>
					Showing <span className='font-medium'>{pageInfo.start}</span> to{' '}
					<span className='font-medium'>{pageInfo.end}</span> of <span className='font-medium'>{totalCount}</span> results
				</div>
				<div className='flex items-center gap-1'>
					<button
						onClick={() => handlePageChange(1)}
						disabled={!hasPrev}
						className='p-2 rounded-md hover:bg-blue-200 dark:hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors'
						title='First page'
					>
						<ChevronsLeft size={16} />
					</button>
					<button
						onClick={() => handlePageChange(pagination.page - 1)}
						disabled={!hasPrev}
						className='p-2 rounded-md hover:bg-blue-200 dark:hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors'
						title='Previous page'
					>
						<ChevronLeft size={16} />
					</button>
					<span className='px-3 text-sm text-blue-600 dark:text-blue-300'>
						Page <span className='font-medium'>{pagination.page}</span> of <span className='font-medium'>{totalPages}</span>
					</span>
					<button
						onClick={() => handlePageChange(pagination.page + 1)}
						disabled={!hasNext}
						className='p-2 rounded-md hover:bg-blue-200 dark:hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors'
						title='Next page'
					>
						<ChevronRight size={16} />
					</button>
					<button
						onClick={() => handlePageChange(totalPages)}
						disabled={!hasNext}
						className='p-2 rounded-md hover:bg-blue-200 dark:hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors'
						title='Last page'
					>
						<ChevronsRight size={16} />
					</button>
				</div>
			</div>
		);
	};

	return (
		<Dialog
			actionButtons={[]}
			minimalHeaderStyle
			sizeX='lg'
			title={<p>Chat History</p>}
			trigger={children || defaultTrigger}
			open={dialogOpen}
			noTitle
			onOpenChange={handleOpenChange}
		>
			<div className='flex flex-col text-[0.85em]'>
				{/* Search bar */}
				<div className='px-4 py-3 border-b border-blue-200 dark:border-blue-700'>
					{canUseGlobalView && (
						<div
							className={`mb-3 ${
								globalView
									? 'rounded-lg border border-blue-100 bg-blue-50/70 px-3 py-2 dark:border-blue-700 dark:bg-blue-900/30'
									: 'flex justify-end'
							}`}
						>
							<div
								className={
									globalView
										? 'mb-2 flex items-center justify-between gap-3'
										: 'inline-flex items-center gap-3 rounded-full border border-blue-100 bg-blue-50/70 px-3 py-1.5 dark:border-blue-700 dark:bg-blue-900/30'
								}
							>
								<div>
									<span className='text-xs font-semibold text-blue-700 dark:text-blue-300'>Global view</span>
									{globalView && (
										<p className='mt-0.5 text-xs font-medium text-blue-600 dark:text-blue-300'>{globalViewDescription}</p>
									)}
								</div>
								<button
									type='button'
									onClick={handleGlobalViewToggle}
									className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
										globalView ? 'bg-blue-600' : 'bg-blue-200 dark:bg-blue-700'
									}`}
									aria-pressed={globalView}
									aria-label='Toggle global chat history view'
								>
									<span
										className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
											globalView ? 'translate-x-5' : 'translate-x-1'
										}`}
									/>
								</button>
							</div>
							{globalView && (
								<>
									<MultiSelect
										label='Filter by users'
										hideLabel
										options={userOptions}
										value={selectedUserOptions}
										onChange={handleUserFilterChange}
										isWorking={isUsersFetching}
										reactSelectProps={{
											placeholder: selectedSystem ? 'Filter by users...' : 'Select a health system first',
											isClearable: true,
										}}
									/>
								</>
							)}
						</div>
					)}
					<TextField
						label='Search conversations'
						hideLabel
						placeholder='Search past conversations...'
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						icon='search'
						iconClass='text-blue-400 dark:text-blue-500'
						sizeY='sm'
						sizeX='full'
						isWorking={isWorking}
					/>
					{!isWorking && isSearchMode && !isSearchLoading && (
						<p className='mt-2 text-sm text-blue-500 dark:text-blue-400'>
							<span>
								Searching for &quot;{debouncedSearchQuery}&quot; • {totalCount} result{totalCount !== 1 ? 's' : ''} found
							</span>
						</p>
					)}
				</div>

				<div className='flex flex-col'>
					<div className='overflow-y-auto max-h-[60vh]'>
						<table className='w-full'>
							<thead className='sticky top-0 bg-white dark:bg-blue-900 z-10 shadow-sm'>
								<tr>
									<th className='text-left px-4 py-3 text-sm font-semibold text-blue-700 dark:text-blue-300 min-w-[200px]'></th>
									<th className='text-left px-4 py-3 text-sm font-semibold text-blue-700 dark:text-blue-300 w-40'></th>
								</tr>
							</thead>
							<tbody>
								{conversations.map((conversation) => {
									const ownerLabel = formatConversationOwner(conversation);
									const contextLabel = formatConversationContext(conversation);

									// Facility/healthsystem context is rendered as a secondary line so titles remain scannable.
									return (
										<tr
											key={conversation.conversation_id}
											onClick={() => handleRowClick(conversation)}
											className={`border-b border-blue-100 dark:border-blue-800 transition-colors ${
												conversation.conversation_id === 'dummy'
													? 'cursor-default'
													: 'hover:bg-blue-50 dark:hover:bg-blue-800/50 cursor-pointer'
											}`}
										>
											<td className='px-4 py-3'>
												<div className='flex flex-col gap-1'>
													<span
														className='whitespace-nowrap block max-w-[300px]'
														title={conversation.title || 'Untitled Conversation'}
													>
														{conversation.title
															? conversation.title.length > 84
																? conversation.title.slice(0, 84) + '...'
																: conversation.title
															: 'Untitled Conversation'}
													</span>
													{canUseGlobalView && globalView && ownerLabel && (
														<span
															className='block max-w-[300px] truncate text-xs text-blue-500/70 dark:text-blue-300/70'
															title={ownerLabel}
														>
															{ownerLabel}
														</span>
													)}
													{contextLabel && (
														<span
															className='block max-w-[300px] truncate text-xs text-blue-500/70 dark:text-blue-300/70'
															title={contextLabel}
														>
															{contextLabel}
														</span>
													)}
												</div>
											</td>
											<td className='whitespace-nowrap px-4 py-3 text-sm text-blue-600 dark:text-blue-400'>
												{conversation.created_at ? formatDate(conversation.created_at) : '-'}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
					{renderPagination()}
				</div>
			</div>
		</Dialog>
	);
}

export default ChatHistoryDialog;
